import type { Session } from '@heroiclabs/nakama-js';
import { getClient } from './client';

/**
 * Authenticate with email and password.
 */
export async function authenticateEmail(
  email: string,
  password: string,
  create?: boolean,
  username?: string,
): Promise<Session> {
  const client = getClient();
  return client.authenticateEmail(email, password, create, username);
}

/**
 * Authenticate with a Google OAuth token.
 */
export async function authenticateGoogle(
  token: string,
  create?: boolean,
  username?: string,
): Promise<Session> {
  const client = getClient();
  return client.authenticateGoogle(token, create, username);
}

/**
 * Authenticate with an Apple ID token.
 */
export async function authenticateApple(
  token: string,
  create?: boolean,
  username?: string,
): Promise<Session> {
  const client = getClient();
  return client.authenticateApple(token, create, username);
}

/**
 * Authenticate with a custom identifier.
 * Use this for Discord, GitHub, or other custom OAuth flows.
 */
export async function authenticateCustom(
  id: string,
  create?: boolean,
  username?: string,
  vars?: Record<string, string>,
): Promise<Session> {
  const client = getClient();
  return client.authenticateCustom(id, create, username, vars);
}

/**
 * Authenticate with a device identifier (anonymous/guest login).
 */
export async function authenticateDevice(
  deviceId: string,
  create?: boolean,
  username?: string,
): Promise<Session> {
  const client = getClient();
  return client.authenticateDevice(deviceId, create, username);
}

/**
 * Refresh an existing session using its refresh token.
 */
export async function refreshSession(session: Session): Promise<Session> {
  const client = getClient();
  return client.sessionRefresh(session);
}

/**
 * Log out the given session, invalidating both tokens.
 */
export async function logout(session: Session): Promise<void> {
  const client = getClient();
  await client.sessionLogout(session, session.token, session.refresh_token);
}

/**
 * Link an email+password to the current account.
 */
export async function linkEmail(
  session: Session,
  email: string,
  password: string,
): Promise<boolean> {
  const client = getClient();
  return client.linkEmail(session, { email, password });
}

/**
 * Link a Google account to the current account.
 */
export async function linkGoogle(
  session: Session,
  token: string,
): Promise<boolean> {
  const client = getClient();
  return client.linkGoogle(session, { token });
}

/**
 * Link a custom ID to the current account.
 */
export async function linkCustom(
  session: Session,
  id: string,
): Promise<boolean> {
  const client = getClient();
  return client.linkCustom(session, { id });
}

/**
 * Unlink an email+password from the current account.
 */
export async function unlinkEmail(
  session: Session,
  email: string,
  password: string,
): Promise<boolean> {
  const client = getClient();
  return client.unlinkEmail(session, { email, password });
}

/**
 * Unlink a Google account from the current account.
 */
export async function unlinkGoogle(
  session: Session,
  token: string,
): Promise<boolean> {
  const client = getClient();
  return client.unlinkGoogle(session, { token });
}

/**
 * Unlink a custom ID from the current account.
 */
export async function unlinkCustom(
  session: Session,
  id: string,
): Promise<boolean> {
  const client = getClient();
  return client.unlinkCustom(session, { id });
}
